/**
 * Data for the PDApps shell (#1484).
 *
 *   - History (personal, every org, links too): `/api/pdapps-shell/history` — pins
 *     plus the recent entries, each already judged by the server as the identity it
 *     was opened as. It follows the ACCOUNT, so it reads the same in every org.
 *   - Shared-with-me links: `/api/pdapps-shell/shared-links` — app links that name
 *     the active identity and would run for it.
 *   - The active org's own apps: the SAME two listings Drive → PDApps reads
 *     (`useProjects` + `useIncomingSharedProjects`), permission-filtered there.
 *
 * Nothing here decides access: every list comes judged from the server, and Run /
 * Share go through the routes and dialogs they always did.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query'
import { request, getActiveOrgEmail, getActiveTenantId } from '@papan/api-client'
import { appDisplayName, isMiniappFolder, isSharedPointer, stripPointerSuffix } from '@papan/types'
import { isMyProject, type ProjectItem } from '../home/data'

/** One tile from the shell's own endpoints (the server's `PdappEntryDto`). */
export interface PdappEntry {
  key: string
  source: 'history' | 'shared'
  kind: 'app' | 'link'
  name: string
  icon: string | null
  orgName: string | null
  pinned: boolean
  openedAt: string | null
  /** Link entries: `public` (anyone with the URL) or `restricted` (the link names who may). */
  audience?: 'public' | 'restricted'
  /** `switchTo`: the identity to make this org's choice before a Run (see the server's `PdappEntryDto`). */
  app?: { tenantId: string; personal: boolean; appId: string; mounted: boolean; switchTo?: string }
}

export interface PdappHistoryPage { pinned: PdappEntry[]; recent: PdappEntry[]; next: string | null }

// Under `['projects', …]` with the identity in the key, so the app-wide identity
// guard (`IDENTITY_SCOPED_KEYS`) covers them: a switch never shows one identity's
// answer under another's key.
const identity = () => [getActiveTenantId(), getActiveOrgEmail()] as const
export const pdappsKeys = {
  all: ['projects', 'pdapps-shell'] as const,
  history: () => ['projects', 'pdapps-shell', 'history', ...identity()] as const,
  sharedLinks: (sameOrg: boolean) => ['projects', 'pdapps-shell', sameOrg ? 'shared-links-org' : 'shared-links', ...identity()] as const,
}

const BASE = '/api/pdapps-shell'
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export const fetchHistoryPage = (before?: string | null) =>
  request<PdappHistoryPage>(`${BASE}/history${before ? `?before=${encodeURIComponent(before)}` : ''}`)

/** Pins + the first page of the recent history. */
export function usePdappHistory(enabled = true) {
  return useQuery<PdappHistoryPage>({
    queryKey: pdappsKeys.history(),
    queryFn: () => fetchHistoryPage(null),
    staleTime: 30_000,
    enabled,
  })
}

/** App links that name the active identity (`sameOrg`: this org's only — the drive page). */
export function useSharedLinkApps(sameOrg: boolean, enabled = true) {
  return useQuery<PdappEntry[]>({
    queryKey: pdappsKeys.sharedLinks(sameOrg),
    queryFn: async () => (await request<{ entries: PdappEntry[] }>(`${BASE}/shared-links${sameOrg ? '?sameOrg=1' : ''}`)).entries,
    staleTime: 30_000,
    enabled,
  })
}

/**
 * Re-read the HISTORY after a write to it (an open, a pin, a removal — the
 * shared-links list doesn't change with any of them). CANCEL first: a read already
 * in flight (the window was just opened) started before this write, and a refresh
 * while a first load is running is folded into that same read instead of starting
 * a new one — the window would show the list without the write until its next
 * refresh. Only a SHOWN window refetches (its query is `enabled` by `deckActive`);
 * a hidden one is just marked stale and reads again when it is shown.
 */
const refresh = (qc: QueryClient) => {
  const queryKey = ['projects', 'pdapps-shell', 'history'] as const
  void qc.cancelQueries({ queryKey })
    .then(() => qc.invalidateQueries({ queryKey }))
}

/**
 * "I opened this app now" (the active org). Fire-and-forget — a lost write only
 * costs the entry; the server checks the app is readable before it stores it.
 */
export function recordAppOpen(queryClient: QueryClient, appId: string): void {
  void request(`${BASE}/history/app`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ appId }) })
    .then(() => refresh(queryClient))
    .catch(() => {})
}

export async function pinActiveApp(qc: QueryClient, appId: string): Promise<void> {
  await request(`${BASE}/pins/app`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ appId }) })
  refresh(qc)
}

export async function pinSharedLink(qc: QueryClient, key: string): Promise<void> {
  await request(`${BASE}/links/${encodeURIComponent(key)}/pin`, { method: 'POST', headers: JSON_HEADERS, body: '{}' })
  refresh(qc)
}

export async function setEntryPinned(qc: QueryClient, key: string, pinned: boolean): Promise<void> {
  await request(`${BASE}/history/${encodeURIComponent(key)}`, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ pinned }) })
  refresh(qc)
}

export async function removeEntry(qc: QueryClient, key: string): Promise<void> {
  await request(`${BASE}/history/${encodeURIComponent(key)}`, { method: 'DELETE' })
  refresh(qc)
}

export async function clearHistory(qc: QueryClient): Promise<void> {
  await request(`${BASE}/history`, { method: 'DELETE' })
  refresh(qc)
}

/**
 * Open a link app. The tab is opened FIRST, on the click (a tab opened after an
 * await is a popup the browser blocks), cut off from this page (`opener = null`),
 * and only then pointed at the address the server hands back after re-judging
 * the link. A refusal closes the tab and throws.
 */
export async function openLinkApp(qc: QueryClient, entry: PdappEntry): Promise<void> {
  const tab = window.open('about:blank', '_blank')
  if (tab) tab.opener = null
  try {
    const { url } = await request<{ url: string }>(`${BASE}/links/${encodeURIComponent(entry.source === 'history' ? 'h' : entry.key)}/open`, {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify(entry.source === 'history' ? { historyId: entry.key } : {}),
    })
    if (tab) tab.location.replace(url)
    else window.location.assign(url)
    refresh(qc)
  } catch (err) {
    tab?.close()
    throw err
  }
}

/**
 * #538 — the identity bar's "Made by": where a link's PDApp sits in the person's own drive, as a
 * drive address that reveals it — or null when it isn't in their drive (the server answers that
 * the same way whatever the reason). The bar hands the link over in this page's FRAGMENT
 * (`/~/apps#locate=<token>`), which no server sees; it is read once and cleared.
 */
export async function locateLinkApp(token: string): Promise<string | null> {
  try {
    return (await request<{ path: string }>(`${BASE}/locate?link=${encodeURIComponent(token)}`)).path
  } catch (err) {
    if ((err as { name?: string } | null)?.name === 'AbortError') throw err
    return null
  }
}

/** The `#locate=<token>` the bar opened this page with, taken off the address — or null. */
export function takeLocateFragment(): string | null {
  const m = /^#locate=([a-z0-9-]{1,120})$/.exec(window.location.hash)
  if (!m) return null
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
  return m[1]!
}

// ─── The active org's own apps (from the drive's listings) ────────────────

/** Mine = in the viewer's own space, or made by them; Shared = someone else's app
 *  in the org they can open, or one another org shared in (a `.shared` pointer). */
export type OrgAppGroup = 'mine' | 'shared'

export function isAppItem(p: ProjectItem): boolean {
  return isMiniappFolder(stripPointerSuffix(p.name))
}

export function orgAppGroupOf(p: ProjectItem, orgEmail: string | null | undefined, isOrg: boolean): OrgAppGroup {
  if (isSharedPointer(p.name)) return 'shared'
  if (isMyProject(p.path, orgEmail, isOrg)) return 'mine'
  // Made by the viewer but kept in a shared folder (a team's, the org's) is still
  // theirs — "Shared with me" means someone ELSE's app. The creator is this org's
  // membership email, the same identity as `orgEmail`.
  const creator = p.owner?.email?.toLowerCase()
  return creator && orgEmail && creator === orgEmail.toLowerCase() ? 'mine' : 'shared'
}

/** The name a person reads: no `.pdapp`, no `.shared`. */
export function appName(p: ProjectItem): string {
  return appDisplayName(stripPointerSuffix(p.name))
}

const matches = (name: string, q: string) => !q || name.toLowerCase().includes(q)

/**
 * The org part of the shell: the drive's app rows (apps only, de-duplicated,
 * admin-only noise hidden as the drive page does by default) split into Mine and
 * Shared, A→Z within each; `query` filters by name.
 */
export function buildOrgApps(
  items: readonly ProjectItem[],
  { orgEmail, isOrg, query = '' }: { orgEmail: string | null | undefined; isOrg: boolean; query?: string },
): Record<OrgAppGroup, ProjectItem[]> {
  const q = query.trim().toLowerCase()
  const seen = new Set<string>()
  const out: Record<OrgAppGroup, ProjectItem[]> = { mine: [], shared: [] }
  for (const p of items) {
    if (!isAppItem(p) || seen.has(p.folderId)) continue
    seen.add(p.folderId)
    if (isOrg && p.hiddenWithoutAdminView) continue
    if (!matches(appName(p), q)) continue
    out[orgAppGroupOf(p, orgEmail, isOrg)].push(p)
  }
  const byName = (a: ProjectItem, b: ProjectItem) => appName(a).localeCompare(appName(b), undefined, { sensitivity: 'base' })
  out.mine.sort(byName)
  out.shared.sort(byName)
  return out
}

/** Filter shell entries by name. */
export function filterEntries(entries: readonly PdappEntry[], query: string): PdappEntry[] {
  const q = query.trim().toLowerCase()
  return q ? entries.filter((e) => matches(e.name, q)) : [...entries]
}
